/**
 * PDF Utility Functions
 * Indian number formatting, amount-in-words, date formatting.
 * Shared across all PDF templates.
 */

/** Format number with Indian comma style: 1,23,456.78 */
export function formatNumber(num) {
  if (num == null || isNaN(num)) return ''
  const fixed = Number(num).toFixed(2)
  const [intPart, dec] = fixed.split('.')
  if (intPart.length <= 3) return intPart + '.' + dec
  const last3 = intPart.slice(-3)
  const rest = intPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')
  return rest + ',' + last3 + '.' + dec
}

/** Format date as DD-MM-YYYY */
export function formatDate(dateString) {
  if (!dateString) return ''
  const d = new Date(dateString)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

/** Convert number to Indian words (Rupees ... and ... Paise only.) */
export function amountInWords(num) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const teens = [
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
    'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ]

  const convert = (n) => {
    if (n < 10) return ones[n]
    if (n < 20) return teens[n - 10]
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '')
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '')
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '')
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '')
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '')
  }

  const rupees = Math.floor(num)
  const paise = Math.round((num - rupees) * 100)
  let words = 'Rupees ' + (rupees === 0 ? 'Zero' : convert(rupees))
  if (paise > 0) words += ' and ' + convert(paise) + ' Paise'
  return words + ' only.'
}

/** Format payment mode for display */
export function formatPaymentMode(mode) {
  const modes = { cash: 'Cash', card: 'Card', upi: 'UPI', cheque: 'Cheque', bank_transfer: 'Bank Transfer' }
  return modes[mode] || mode || 'Cash'
}

/** Format unit type abbreviation */
export function formatUnitType(unitType) {
  const map = { pcs: 'PCS', box: 'BOX', kg: 'KG', g: 'G', l: 'L', ml: 'ML', pkt: 'PKT', set: 'SET', nos: 'NOS', piece: 'PCS' }
  return map[unitType?.toLowerCase()] || unitType?.toUpperCase() || ''
}

/** GST state code to state name */
const GST_STATE_MAP = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana',
  '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam',
  '19': 'West Bengal', '20': 'Jharkhand', '21': 'Odisha',
  '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '25': 'Daman & Diu', '26': 'Dadra & Nagar Haveli', '27': 'Maharashtra',
  '28': 'Andhra Pradesh', '29': 'Karnataka', '30': 'Goa',
  '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman & Nicobar', '36': 'Telangana',
  '37': 'Andhra Pradesh', '38': 'Ladakh',
}

export function getStateName(code) {
  if (!code) return ''
  return GST_STATE_MAP[code] || code
}

/** Get place of supply from customer GSTIN or business state */
export function getPlaceOfSupply(customerGstin, businessState) {
  const custState = customerGstin ? customerGstin.substring(0, 2) : null
  const stateCode = custState || businessState
  if (!stateCode) return ''
  return `${stateCode} - ${getStateName(stateCode)}`
}
